import { Injectable, OnDestroy, inject } from '@angular/core';
import { Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { ApiConfiguration } from '../api/api-configuration';
import { NotificationsService } from '../api/services/notifications.service';
import { ClientUpdatedEvent } from '../api/models/client-updated-event';
import { CommentChangedEvent } from '../api/models/comment-changed-event';
import { EmailCreatedEvent } from '../api/models/email-created-event';
import { EmailUpdatedEvent } from '../api/models/email-updated-event';
import { EndCustomerUpdatedEvent } from '../api/models/end-customer-updated-event';
import { ExportChangedEvent } from '../api/models/export-changed-event';
import { HeartbeatEvent } from '../api/models/heartbeat-event';
import { InvoiceAttachmentCreatedEvent } from '../api/models/invoice-attachment-created-event';
import { InvoiceAttachmentUpdatedEvent } from '../api/models/invoice-attachment-updated-event';
import { InvoicePresenceChangedEvent } from '../api/models/invoice-presence-changed-event';
import { InvoiceUpdatedEvent } from '../api/models/invoice-updated-event';
import { InvoiceWorkflowActivityEvent } from '../api/models/invoice-workflow-activity-event';
import { PaymentCreatedEvent } from '../api/models/payment-created-event';
import { PaymentHistoryCreatedEvent } from '../api/models/payment-history-created-event';
import { PaymentStatusUpdatedEvent } from '../api/models/payment-status-updated-event';
import { TextMessageUpdatedEvent } from '../api/models/text-message-updated-event';
import { WorkflowStatusEvent } from '../api/models/workflow-status-event';

/**
 * Every SSE event-type the service listens for. Each name corresponds
 * to an OpenAPI-generated payload interface in `../api/models/` and is
 * one branch of the {@link RealtimeEvent} discriminated union (keyed by
 * `event_type`). This single array drives the `addEventListener`
 * registration loop on the native EventSource — events outside this set
 * never reach `events$`, since named SSE events do not fire `onmessage`.
 * Adding a new event type means adding its `event_type` string here and
 * its generated interface to the union below.
 */
const KNOWN_EVENT_TYPES = [
  'invoice_updated',
  'client_updated',
  'end_customer_updated',
  'invoice_attachment_created',
  'invoice_attachment_updated',
  'email_created',
  'email_updated',
  'payment_created',
  'payment_status_updated',
  'payment_history_created',
  'text_message_updated',
  'comment_changed',
  'heartbeat',
  'invoice_presence_changed',
  'invoice_workflow_activity',
  'workflow_status',
  'export_changed',
] as const;

export type RealtimeEvent =
  | WorkflowStatusEvent
  | InvoiceWorkflowActivityEvent
  | InvoicePresenceChangedEvent
  | ExportChangedEvent
  | HeartbeatEvent
  | TextMessageUpdatedEvent
  | EmailCreatedEvent
  | EmailUpdatedEvent
  | PaymentStatusUpdatedEvent
  | PaymentCreatedEvent
  | PaymentHistoryCreatedEvent
  | ClientUpdatedEvent
  | EndCustomerUpdatedEvent
  | InvoiceAttachmentCreatedEvent
  | InvoiceAttachmentUpdatedEvent
  | InvoiceUpdatedEvent
  | CommentChangedEvent;

type ScopeType = 'invoice' | 'client' | 'end_customer';
type ActivityType = 'viewing' | 'editing';

interface SubscriptionRecord {
  scopeType: ScopeType;
  scopeId: number;
  subscriptionId: string;
  activityType?: ActivityType;
  // Set when the consumer unsubscribes before the server has returned a
  // subscriptionId. The POST `next` callback then fires the DELETE
  // immediately after stamping the real id, so the server-side row does
  // not leak past the consumer's lifetime.
  pendingDelete?: boolean;
}

/**
 * Owns one native `EventSource` per browser tab and the corresponding
 * server-side subscription set. Established lazily on first subscriber.
 * Page components attach scope subscriptions via `subscribeToInvoice`
 * etc.; the returned RxJS Subscription releases the server-side row on
 * unsubscribe. Tab destruction (logout, tab close) cascades the
 * connection drop on the backend.
 *
 * Authentication piggybacks on the BFF session cookie (same-origin
 * `/api/...`), so no request headers need to be set on the underlying
 * stream — which is exactly why native `EventSource` fits.
 */
@Injectable({
  providedIn: 'root',
})
export class RealtimeNotificationsService implements OnDestroy {
  private readonly apiConfig = inject(ApiConfiguration);
  private readonly notificationsApi = inject(NotificationsService);

  // One id per service instance — i.e. one per browser tab. Generated
  // up front so subscribeTo*() calls made before the first event arrive
  // can still reference it.
  public readonly pageInstanceId: string = uuidv4();

  private readonly events = new Subject<RealtimeEvent>();
  public readonly events$: Observable<RealtimeEvent> = this.events.asObservable();

  private eventSource: EventSource | null = null;
  private readonly destroyed$ = new Subject<void>();

  // Resolves once the EventSource has reported `open` for the current
  // connection — meaning the server-side CreateConnection has executed
  // and the sse_connections row exists. Subscription POSTs gate on this
  // to avoid the cold-connection FK violation when the POST races
  // ahead of the GET.
  //
  // Cached for the service lifetime — never re-armed. The server's
  // CreateConnection is idempotent (`ON CONFLICT (page_instance_id)
  // DO UPDATE`), so browser auto-reconnects preserve the row under the
  // same `page_instance_id` and the cached "ready" signal remains
  // valid.
  private handshakeReady$ = new ReplaySubject<void>(1);

  private readonly openSubscriptions: SubscriptionRecord[] = [];

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.closeStream();
  }

  public subscribeToInvoice(invoiceId: number, activityType: ActivityType = 'viewing'): Subscription {
    return this.attachScope('invoice', invoiceId, activityType);
  }

  public subscribeToClient(clientId: number): Subscription {
    return this.attachScope('client', clientId);
  }

  public subscribeToEndCustomer(endCustomerId: number): Subscription {
    return this.attachScope('end_customer', endCustomerId);
  }

  /**
   * Switch an open invoice subscription's `activity_type` (e.g. when
   * the user starts editing). Resolves once the server has applied the
   * PATCH so callers can chain UI updates if needed.
   */
  public async updateInvoiceActivity(invoiceId: number, activityType: ActivityType): Promise<void> {
    const record = this.findScope('invoice', invoiceId);
    if (!record) {
      // Caller asked to update a subscription that doesn't exist on this
      // tab. Surface as a warn — don't throw, the caller likely just
      // wants the UI side-effect.
      console.warn(`[realtime] updateInvoiceActivity: no open subscription for invoice ${invoiceId}`);
      return;
    }
    record.activityType = activityType;
    await this.notificationsApi
      .updateNotificationSubscription({ subscriptionID: record.subscriptionId, body: { activity_type: activityType } })
      .toPromise();
  }

  private attachScope(scopeType: ScopeType, scopeId: number, activityType?: ActivityType): Subscription {
    this.ensureConnected();

    const placeholder: SubscriptionRecord = { scopeType, scopeId, subscriptionId: '', activityType };
    this.openSubscriptions.push(placeholder);

    const sub = new Subscription(() => this.detachScope(placeholder));

    // Gate the POST on the EventSource `open` event — by then the
    // server has flushed response headers, which happens only after
    // CreateConnection commits, so the subscription INSERT will not
    // hit the FK violation. `handshakeReady$` is a ReplaySubject so
    // subscribeTo*() calls that arrive after the handshake fire
    // immediately.
    this.handshakeReady$.pipe(take(1), takeUntil(this.destroyed$)).subscribe(() => {
      this.notificationsApi
        .addNotificationSubscription({
          body: {
            page_instance_id: this.pageInstanceId,
            scope_type: scopeType,
            scope_id: scopeId,
            activity_type: scopeType === 'invoice' ? (activityType ?? 'viewing') : null,
          },
        })
        .subscribe({
          next: (resp) => {
            placeholder.subscriptionId = resp.subscription_id;
            // The consumer unsubscribed before the POST returned —
            // flush the deferred DELETE now that we have an id.
            if (placeholder.pendingDelete) {
              this.notificationsApi
                .removeNotificationSubscription({ subscriptionID: resp.subscription_id })
                .subscribe({
                  error: (err) =>
                    console.error(`[realtime] failed to remove deferred subscription ${resp.subscription_id}`, err),
                });
            }
          },
          error: (err) => console.error(`[realtime] failed to add subscription ${scopeType}:${scopeId}`, err),
        });
    });

    return sub;
  }

  private detachScope(record: SubscriptionRecord): void {
    const idx = this.openSubscriptions.indexOf(record);
    if (idx >= 0) {
      this.openSubscriptions.splice(idx, 1);
    }
    if (record.subscriptionId) {
      this.notificationsApi
        .removeNotificationSubscription({ subscriptionID: record.subscriptionId })
        .subscribe({
          error: (err) => console.error(`[realtime] failed to remove subscription ${record.subscriptionId}`, err),
        });
      return;
    }
    // POST is still in flight — mark the record so the next callback
    // can fire the DELETE once we know the subscription id.
    record.pendingDelete = true;
  }

  private findScope(scopeType: ScopeType, scopeId: number): SubscriptionRecord | undefined {
    return this.openSubscriptions.find((r) => r.scopeType === scopeType && r.scopeId === scopeId);
  }

  private ensureConnected(): void {
    if (this.eventSource) {
      return;
    }
    this.openStream();
  }

  private openStream(): void {
    const url = `${this.apiConfig.rootUrl}/notifications/subscribe?page_instance_id=${this.pageInstanceId}`;
    const es = new EventSource(url);

    es.onopen = () => {
      // The browser fires `open` after the response headers arrive,
      // which the server emits only after CreateConnection commits.
      // `handshakeReady$` is a ReplaySubject(1) so the latest "ready"
      // value is replayed to subscribers that attach after this
      // point. Auto-reconnects re-fire onopen against a fresh
      // server-side connection row; the cached ready value remains
      // valid because the server has reseated the row before
      // flushing headers.
      this.handshakeReady$.next();
    };

    es.onerror = (event) => {
      // The browser auto-reconnects on transient errors. If
      // readyState transitions to CLOSED the connection is gone
      // permanently — log so an operator can see something happened.
      if (es.readyState === EventSource.CLOSED) {
        console.error('[realtime] SSE stream closed permanently', event);
      }
    };

    for (const type of KNOWN_EVENT_TYPES) {
      es.addEventListener(type, (event) => this.dispatch(event as MessageEvent));
    }

    this.eventSource = es;
  }

  private closeStream(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  private dispatch(event: MessageEvent): void {
    const eventType = event.type;
    let payload: Record<string, unknown> = {};
    if (event.data) {
      try {
        payload = JSON.parse(event.data) as Record<string, unknown>;
      } catch {
        // Heartbeats and other non-JSON payloads — keep an empty body.
      }
    }
    const realtime = {
      ...payload,
      event_type: eventType,
    } as unknown as RealtimeEvent;
    this.events.next(realtime);
  }
}
